import api from './api';

export interface ThemeSettings {
  mode: 'light' | 'dark';
  primaryColor?: string;
  density?: 'comfortable' | 'compact' | 'standard';
}

export interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  desktop: boolean;
  orderUpdates: boolean;
  deliveryAlerts: boolean;
  inventoryAlerts: boolean;
  billingAlerts: boolean;
  systemAlerts: boolean;
}

export interface WorkflowDefaults {
  defaultLandingPage?: string;
  /** PRX-124: true when the user picked `defaultLandingPage` in Profile; see `ownLandingPage`. */
  landingPageChosen?: boolean;
  preferredQueues?: string[];
  autoFilters?: Record<string, any>;
  defaultFormValues?: Record<string, any>;
  /** Release announcements this user dismissed: announcement id → YYYY-MM-DD. */
  dismissedAnnouncements?: Record<string, string>;
}

export interface PrinterDevice {
  name: string;
  uid: string;
  connection: string;
  deviceType: string;
  version: number;
  provider: string;
  manufacturer: string;
}

export interface PrinterSettings {
  enabled: boolean;
  printers: {
    vial: PrinterDevice | null;
    receipt: PrinterDevice | null;
    delivery: PrinterDevice | null;
  };
}

/** One grid's saved view (UAT 2026-07-09) — keyed per grid, e.g. "queue:billing". */
export interface GridSettingEntry {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  columnVisibilityModel?: Record<string, boolean>;
  /** Drag-and-drop column order (GP-1121) — ordered list of column field ids. */
  columnOrder?: string[];
  pageSize?: number;
  /** PRX-137: hide PMS-deleted rows (Patient 360 prescriptions). Absent means on. */
  hideDeleted?: boolean;
}

export interface UserPreferences {
  id: string;
  auth0Id: string;
  pharmacyId: string;
  themeSettings: ThemeSettings;
  notificationPreferences: NotificationPreferences;
  workflowDefaults: WorkflowDefaults;
  printerSettings: PrinterSettings;
  gridSettings?: Record<string, GridSettingEntry>;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateUserPreferencesDto {
  themeSettings?: Partial<ThemeSettings>;
  notificationPreferences?: Partial<NotificationPreferences>;
  workflowDefaults?: Partial<WorkflowDefaults>;
  printerSettings?: Partial<PrinterSettings>;
}

class UserPreferencesService {
  /**
   * Get user preferences for the current pharmacy
   */
  async getUserPreferences(pharmacyId: string): Promise<UserPreferences> {
    const response = await api.get<UserPreferences>('/api/user-preferences', {
      params: { pharmacyId },
    });
    return response.data;
  }

  /**
   * Create user preferences for the current pharmacy
   */
  async createUserPreferences(
    pharmacyId: string,
    preferences: UpdateUserPreferencesDto,
  ): Promise<UserPreferences> {
    const response = await api.post<UserPreferences>('/api/user-preferences', preferences, {
      params: { pharmacyId },
    });
    return response.data;
  }

  /**
   * Update user preferences for the current pharmacy
   */
  async updateUserPreferences(
    pharmacyId: string,
    preferences: UpdateUserPreferencesDto,
  ): Promise<UserPreferences> {
    const response = await api.put<UserPreferences>('/api/user-preferences', preferences, {
      params: { pharmacyId },
    });
    return response.data;
  }

  /**
   * Merge ONE grid's saved view (columns / sorting / page size).
   */
  async updateGridSetting(
    pharmacyId: string,
    gridKey: string,
    entry: GridSettingEntry,
  ): Promise<UserPreferences> {
    const response = await api.put<UserPreferences>(
      `/api/user-preferences/grid/${encodeURIComponent(gridKey)}`,
      entry,
      { params: { pharmacyId } },
    );
    return response.data;
  }

  /**
   * Delete user preferences for the current pharmacy
   */
  async deleteUserPreferences(pharmacyId: string): Promise<void> {
    await api.delete('/api/user-preferences', {
      params: { pharmacyId },
    });
  }
}

const userPreferencesService = new UserPreferencesService();
export default userPreferencesService;
